import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Loader2, Plus, Edit, Users, DollarSign, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell, SortIcon, createSortHelpers } from '../components/AdminShared';

const COUNTRIES = ['Australia', 'Canada', 'United Kingdom', 'US'].sort();

export default function AdminOrgsPage() {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [feeSchedules, setFeeSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [form, setForm] = useState({ name: '', country: 'US', plan: 'professional', contact_name: '', contact_address: '', contact_phone: '', contact_email: '', fee_schedule_id: '' });
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [creditsOrg, setCreditsOrg] = useState(null);
  const [orgCredits, setOrgCredits] = useState(null);
  const [freeCreditsAmount, setFreeCreditsAmount] = useState('');
  const [freeCreditsReason, setFreeCreditsReason] = useState('Free allocation');
  const [adjusting, setAdjusting] = useState(false);
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const loadOrgs = useCallback(async () => {
    try {
      const [oRes, fRes] = await Promise.all([adminAPI.listOrgs(), adminAPI.listFeeSchedules({})]);
      setOrgs(oRes.data);
      setFeeSchedules(fRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const { toggleSort } = createSortHelpers(sortCol, sortDir, setSortCol, setSortDir);

  const sorted = useMemo(() => {
    return [...orgs].sort((a, b) => {
      let va = '', vb = '';
      if (sortCol === 'name') { va = a.name || ''; vb = b.name || ''; }
      else if (sortCol === 'contact') { va = a.contact_name || ''; vb = b.contact_name || ''; }
      else if (sortCol === 'country') { va = a.country || ''; vb = b.country || ''; }
      else if (sortCol === 'plan') { va = a.plan || ''; vb = b.plan || ''; }
      else if (sortCol === 'credits') { va = String(a.credit_balance || 0).padStart(8, '0'); vb = String(b.credit_balance || 0).padStart(8, '0'); }
      else if (sortCol === 'spent_total') { va = String(a.credits_spent_total || 0).padStart(8, '0'); vb = String(b.credits_spent_total || 0).padStart(8, '0'); }
      else if (sortCol === 'spent_month') { va = String(a.credits_spent_last_month || 0).padStart(8, '0'); vb = String(b.credits_spent_last_month || 0).padStart(8, '0'); }
      else if (sortCol === 'clients') { va = String(a.client_count || 0).padStart(8, '0'); vb = String(b.client_count || 0).padStart(8, '0'); }
      const cmp = va.localeCompare(vb);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [orgs, sortCol, sortDir]);

  const openCreate = () => { setEditOrg(null); setForm({ name: '', country: 'US', plan: 'professional', contact_name: '', contact_address: '', contact_phone: '', contact_email: '', fee_schedule_id: '' }); setShowDialog(true); };
  const openEdit = (org) => { setEditOrg(org); setForm({ name: org.name, country: org.country, plan: org.plan, contact_name: org.contact_name || '', contact_address: org.contact_address || '', contact_phone: org.contact_phone || '', contact_email: org.contact_email || '', fee_schedule_id: '' }); setShowDialog(true); };

  const handleSubmit = async () => {
    try {
      const payload = { ...form };
      if (!payload.fee_schedule_id) delete payload.fee_schedule_id;
      if (editOrg) { delete payload.fee_schedule_id; await adminAPI.updateOrg(editOrg.id, payload); }
      else await adminAPI.createOrg(payload);
      setShowDialog(false); loadOrgs();
    } catch (e) { console.error(e); }
  };

  const openOrgCredits = async (org) => {
    setCreditsOrg(org);
    setFreeCreditsAmount(''); setFreeCreditsReason('Free allocation');
    try { const res = await adminAPI.getOrgCredits(org.id); setOrgCredits(res.data); }
    catch (e) { setOrgCredits({ balance: 0, transactions: [] }); }
    setShowCreditsDialog(true);
  };

  const handleAddFreeCredits = async () => {
    const amount = parseInt(freeCreditsAmount);
    if (!amount || amount <= 0 || !creditsOrg) return;
    setAdjusting(true);
    try {
      const res = await adminAPI.adjustOrgCredits(creditsOrg.id, { amount, reason: `Free allocation: ${freeCreditsReason}` });
      setOrgCredits(prev => ({ ...prev, balance: res.data.balance }));
      toast.success(`Added ${amount} free credits to ${creditsOrg.name}`);
      setFreeCreditsAmount('');
      // Refresh transactions
      const updated = await adminAPI.getOrgCredits(creditsOrg.id);
      setOrgCredits(updated.data);
    } catch (e) { toast.error('Failed to add credits'); }
    setAdjusting(false);
  };

  const getOrgFeeSchedule = (orgId) => feeSchedules.find(f => f.org_id === orgId);

  if (loading) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell
      title="Organizations"
      subtitle="Manage client organizations"
      testId="admin-orgs-page"
      actions={
        <Button onClick={openCreate} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-org-button"><Plus className="w-4 h-4 mr-2" />New Organization</Button>
      }
    >

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-org-name">Name<SortIcon col="name" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('contact')}>Contact<SortIcon col="contact" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('country')}>Country<SortIcon col="country" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('plan')}>Plan<SortIcon col="plan" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('credits')}>Credits<SortIcon col="credits" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('spent_total')}>Spent Total<SortIcon col="spent_total" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('spent_month')}>Last 30d<SortIcon col="spent_month" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('clients')}>Clients<SortIcon col="clients" sortCol={sortCol} sortDir={sortDir} /></TableHead>
              <TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {sorted.map(org => (
                <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                  <TableCell>
                    <div className="cursor-pointer hover:underline" onClick={() => openEdit(org)} data-testid={`org-name-click-${org.id}`}>
                      <p className="font-medium">{org.name}</p><p className="text-xs text-muted-foreground font-mono">{org.id}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {org.contact_name ? (
                      <div className="text-sm"><p>{org.contact_name}</p><p className="text-xs text-muted-foreground">{org.contact_email}</p></div>
                    ) : <span className="text-xs text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{org.country}</Badge></TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs capitalize">{org.plan}</Badge></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="font-mono text-xs gap-1 h-7" onClick={() => openOrgCredits(org)} data-testid={`org-credits-${org.id}`}>
                      <Coins className="w-3 h-3" />{org.credit_balance ?? 0}
                    </Button>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{org.credits_spent_total ?? 0}</TableCell>
                  <TableCell className="font-mono text-xs">{org.credits_spent_last_month ?? 0}</TableCell>
                  <TableCell className="font-mono">{org.client_count || 0}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(org)} title="Edit" data-testid={`edit-org-${org.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => {
                        const fs = getOrgFeeSchedule(org.id);
                        navigate(fs ? `/admin/fee-schedules?org_id=${org.id}` : '/admin/fee-schedules');
                      }} title="Fee schedule" data-testid={`fee-schedule-${org.id}`}><DollarSign className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/clients?org_id=${org.id}`)} title="View clients" data-testid={`view-clients-${org.id}`}><Users className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {orgs.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No organizations</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        {/* Create/Edit Org Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg" data-testid="org-dialog">
            <DialogHeader><DialogTitle>{editOrg ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
              <DialogDescription>Organization details and key contact information</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2"><Label>Organization Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="org-name-input" placeholder="Acme Legal" /></div>
                <div className="space-y-2"><Label>Country</Label>
                  <Select value={form.country} onValueChange={v => setForm(p => ({ ...p, country: v }))}>
                    <SelectTrigger data-testid="org-country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-2"><Label>Plan</Label>
                  <Select value={form.plan} onValueChange={v => setForm(p => ({ ...p, plan: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="professional">Professional</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent></Select></div>
              </div>
              {!editOrg && (
                <div className="space-y-2">
                  <Label>Fee Schedule (optional)</Label>
                  <Select value={form.fee_schedule_id} onValueChange={v => setForm(p => ({ ...p, fee_schedule_id: v === '_none' ? '' : v }))}>
                    <SelectTrigger data-testid="org-fee-schedule-select"><SelectValue placeholder="Select existing or create later" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None - create manually later</SelectItem>
                      {feeSchedules.sort((a, b) => a.name.localeCompare(b.name)).map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Assign an existing fee schedule or create one later</p>
                </div>
              )}
              <div className="border-t pt-4"><p className="text-sm font-semibold mb-3 text-muted-foreground">Key Contact Information</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2"><Label>Contact Name</Label><Input value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} data-testid="org-contact-name" /></div>
                  <div className="col-span-2 space-y-2"><Label>Contact Address</Label><Input value={form.contact_address} onChange={e => setForm(p => ({ ...p, contact_address: e.target.value }))} data-testid="org-contact-address" /></div>
                  <div className="space-y-2"><Label>Contact Phone</Label><Input value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} data-testid="org-contact-phone" /></div>
                  <div className="space-y-2"><Label>Contact Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} data-testid="org-contact-email" /></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={!form.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-org">{editOrg ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Org Credits Dialog with Free Allocation */}
        <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
          <DialogContent className="max-w-md" data-testid="org-credits-dialog">
            <DialogHeader><DialogTitle>Credits - {creditsOrg?.name}</DialogTitle>
              <DialogDescription>View balance and add free credit allocations</DialogDescription></DialogHeader>
            {orgCredits && (
              <div className="space-y-4 py-2">
                <div className="rounded-lg border p-4 text-center">
                  <p className="text-4xl font-bold font-mono">{orgCredits.balance}</p>
                  <p className="text-sm text-muted-foreground mt-1">Available Credits</p>
                </div>

                {/* Add Free Credits */}
                <div className="rounded-lg border p-4 space-y-3">
                  <h4 className="text-sm font-semibold">Add Free Credits</h4>
                  <div className="flex gap-2">
                    <Input type="number" min="1" value={freeCreditsAmount} onChange={e => setFreeCreditsAmount(e.target.value)} placeholder="Amount" className="w-24" data-testid="free-credits-amount" />
                    <Input value={freeCreditsReason} onChange={e => setFreeCreditsReason(e.target.value)} placeholder="Reason" className="flex-1" data-testid="free-credits-reason" />
                    <Button size="sm" onClick={handleAddFreeCredits} disabled={adjusting || !freeCreditsAmount}
                      className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="add-free-credits-btn">
                      {adjusting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>
                </div>

                {orgCredits.transactions?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">Recent Transactions</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {orgCredits.transactions.slice(0, 15).map(t => (
                        <div key={t.id} className="flex items-center justify-between text-sm border-b pb-2">
                          <div>
                            <p className="text-xs">{t.description}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{new Date(t.created_at).toLocaleString()}</p>
                          </div>
                          <span className={`font-mono font-semibold ${t.amount > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {t.amount > 0 ? '+' : ''}{t.amount}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setShowCreditsDialog(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
    </AdminPageShell>
  );
}
